import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { NavItem, NavItemInsert, NavItemUpdate } from "@/types/navigation"
import { queuePublicCachePurge } from "@/lib/publicCache"

export interface UseNavigationResult {
  items: NavItem[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Fetch navigation items for an org. For public site nav and nav editor.
 */
export function useNavigation(orgId: string | null): UseNavigationResult {
  const [items, setItems] = useState<NavItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: e } = await supabase
      .from("org_navigation")
      .select("*")
      .eq("org_id", orgId)
      .order("order_index", { ascending: true })
    if (e) {
      setError(e.message)
      setItems([])
    } else {
      setItems((data ?? []) as NavItem[])
    }
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { items, loading, error, refresh }
}

export interface UseNavigationMutationsResult {
  addNavItem: (input: NavItemInsert) => Promise<NavItem | null>
  updateNavItem: (id: string, updates: NavItemUpdate) => Promise<boolean>
  deleteNavItem: (id: string) => Promise<boolean>
  reorderNavItems: (orderedIds: string[]) => Promise<boolean>
  loading: boolean
  error: string | null
}

/**
 * Mutations for navigation items. Use with useNavigation for refresh.
 */
export function useNavigationMutations(orgId: string | null): UseNavigationMutationsResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const addNavItem = useCallback(
    async (input: NavItemInsert): Promise<NavItem | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)
      const payload = { ...input, org_id: orgId }
      let { data, error: e } = await supabase
        .from("org_navigation")
        .insert(payload)
        .select()
        .single()
      // Retry without open_in_new_tab when schema lacks the column (migration not yet applied)
      if (e?.message?.includes("open_in_new_tab") && "open_in_new_tab" in payload) {
        const { open_in_new_tab: _, ...fallback } = payload
        const res = await supabase.from("org_navigation").insert(fallback).select().single()
        e = res.error
        data = res.data
      }
      setLoading(false)
      if (e) {
        setError(e.message)
        return null
      }
      queuePublicCachePurge(orgId, { type: "nav-change" })
      return data as NavItem
    },
    [orgId]
  )

  const updateNavItem = useCallback(
    async (id: string, updates: NavItemUpdate): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      let { error: e } = await supabase
        .from("org_navigation")
        .update(updates)
        .eq("id", id)
        .eq("org_id", orgId)
      // Retry without open_in_new_tab when schema lacks the column (migration not yet applied)
      if (e?.message?.includes("open_in_new_tab") && "open_in_new_tab" in updates) {
        const { open_in_new_tab: _, ...fallback } = updates
        if (Object.keys(fallback).length > 0) {
          const res = await supabase
            .from("org_navigation")
            .update(fallback)
            .eq("id", id)
            .eq("org_id", orgId)
          e = res.error
        }
      }
      setLoading(false)
      if (e) {
        setError(e.message)
        return false
      }
      queuePublicCachePurge(orgId, { type: "nav-change" })
      return true
    },
    [orgId]
  )

  const deleteNavItem = useCallback(
    async (id: string): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { error: e } = await supabase
        .from("org_navigation")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
      setLoading(false)
      if (e) {
        setError(e.message)
        return false
      }
      queuePublicCachePurge(orgId, { type: "nav-change" })
      return true
    },
    [orgId]
  )

  const reorderNavItems = useCallback(
    async (orderedIds: string[]): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      for (let i = 0; i < orderedIds.length; i++) {
        const { error: e } = await supabase
          .from("org_navigation")
          .update({ order_index: i })
          .eq("id", orderedIds[i])
          .eq("org_id", orgId)
        if (e) {
          setError(e.message)
          setLoading(false)
          return false
        }
      }
      setLoading(false)
      queuePublicCachePurge(orgId, { type: "nav-change" })
      return true
    },
    [orgId]
  )

  return {
    addNavItem,
    updateNavItem,
    deleteNavItem,
    reorderNavItems,
    loading,
    error,
  }
}
